import { Controller, useFormContext } from 'react-hook-form';
import {
  Clone,
  DatePicker,
  Delete,
  TimeWindowControl,
  Warning,
} from 'components';
import { useState, useEffect } from 'react';
import { CircleButton } from 'components/CircleButton';
import { Select } from 'components/Select';
import { Api } from 'api';
import { dateToFormat } from 'helpers/formatters/date';
import { AssetWeightedPreviewItem } from 'types/AssetWeighted';
import { TimeWindowRefData } from 'hooks/useTimeWindowReferenceData';
import { useFormErrors } from 'hooks';

type Props = {
  index: number;
  isOpened: boolean;
  onClone: Function;
  onRemove: Function;
  onRefresh: Function;
  refreshDates: Function;
  referenceData: TimeWindowRefData;
  timeWindow: Record<string, any>;
  handleToggleHeaders: Function;
};

export const assetSelectionsLoaderFieldName = 'assetSelectionsData';

export const AssetWeightedTimeWindowHeader = ({
  isOpened,
  onClone,
  onRemove,
  refreshDates,
  index,
  referenceData,
  timeWindow,
  handleToggleHeaders,
}: Props) => {
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();
  const [selectionSet, setSelectionSet] = useState<number | undefined>();

  const timeWindows = getValues('timeWindows');
  const fieldName = `timeWindows[${index}]`;

  const assetSelectionsLoaderField = `timeWindows[${index}].${assetSelectionsLoaderFieldName}`;
  const weightingSelectionFieldName = `${fieldName}.weightingComponentsSelectionSet`;
  const lineItemsFieldName = `${fieldName}.lineItems`;
  const { getErrorMessage, hasErrors } = useFormErrors({
    errors,
    fieldName,
    index,
  });
  const {
    data: assetsBySelectionSet,
    isLoading: isLoadingAssetsBySelectionSet,
  } = Api.lookup.GetAssetsBySelectionSet(selectionSet);
  const {
    mutate,
    data: timeWindowPreview,
    isLoading: isPreviewLoading,
  } = Api.benchmarks.GetAssetWeightedTimeWindowPreview();
  const { data: assetSelections = [], isLoading: assetSelectionsLoading } =
    Api.lookup.GetAssetSelectionSets();

  useEffect(() => {
    setValue(
      assetSelectionsLoaderField,
      isLoadingAssetsBySelectionSet || isPreviewLoading
    );
  }, [isLoadingAssetsBySelectionSet, isPreviewLoading]);

  useEffect(() => {
    const resourceIds = assetsBySelectionSet?.map((asset) => asset.resourceId);
    if (resourceIds && resourceIds.length > 0) {
      const currencyCode = getValues('baseCurrencyCode');
      const asOfDate = dateToFormat(getValues(`${fieldName}.fromDate`));
      mutate({
        currencyCode,
        asOfDate,
        resourceIds: resourceIds,
      });
    }
  }, [assetsBySelectionSet]);

  useEffect(() => {
    const lineItems = getValues(lineItemsFieldName);
    const lineItemsMap = new Map(
      lineItems.map((v: any) => [v.weightingComponent, v])
    );
    if (timeWindowPreview) {
      const newAssets = timeWindowPreview?.data
        ?.filter(
          (asset) => !lineItemsMap.has(asset.weightingComponent?.resourceId)
        )
        .map((asset: AssetWeightedPreviewItem) => {
          return {
            returnComponent:
              asset.returnComponent?.resourceId ??
              asset.returnComponent?.benchmarkId ??
              null,
            returnComponentName: asset.returnComponent?.name,
            weightingComponent: asset.weightingComponent?.resourceId,
            weightingComponentName: asset.weightingComponent?.name,
          };
        });
      setValue(lineItemsFieldName, lineItems.concat(newAssets), {
        shouldDirty: true,
      });
    }
  }, [timeWindowPreview]);

  return (
    <div
      key={index}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <div
        style={{
          maxWidth: '90%',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '10px',
          paddingBottom: '6px',
        }}
      >
        <TimeWindowControl label='From Date'>
          <Controller
            name={`${fieldName}.fromDate`}
            control={control}
            render={({ field: { onChange, value } }) => {
              const dateValue =
                typeof value === 'string' ? new Date(value) : value;

              return (
                <DatePicker
                  key='fromDate'
                  testid={`${fieldName}.fromDate`}
                  onChange={onChange}
                  onCalendarClose={() => refreshDates()}
                  selected={dateValue}
                  hasError={getErrorMessage(`${fieldName}.fromDate`)}
                  style={{ width: 120 }}
                  size='sm'
                />
              );
            }}
          />
        </TimeWindowControl>
        <TimeWindowControl label='To Date'>
          <Controller
            control={control}
            name={`${fieldName}.toDate`}
            render={({ field: { onChange, value } }) => {
              return (
                <DatePicker
                  key='toDate'
                  disabled
                  onChange={onChange}
                  testid={`${fieldName}.toDate`}
                  selected={value}
                  hasError={getErrorMessage(`${fieldName}.toDate`)}
                  style={{ width: 120, color: '#8795A2' }}
                  size='sm'
                />
              );
            }}
          />
        </TimeWindowControl>
        <TimeWindowControl label='Add Weighting Components'>
          <Select
            placeholder='Search for selection set'
            testid={weightingSelectionFieldName}
            onChange={(v: any) => {
              setSelectionSet(v.value);
              handleToggleHeaders({
                index: timeWindow.toggleId,
                forceOpen: true,
              });
            }}
            options={assetSelections}
            style={{ width: 250 }}
            size='sm'
            isLoading={assetSelectionsLoading}
          />
        </TimeWindowControl>
      </div>
      <div
        style={{
          flex: '0 1 auto',
          alignSelf: 'center',
          justifyContent: 'end',
          display: 'flex',
          flexDirection: 'row',
          width: '200px',
        }}
      >
        {isOpened && (
          <>
            <CircleButton
              key='clone'
              data-testid={`${fieldName}-clone`}
              icon={<Clone size='7px' />}
              title='Clone'
              onClick={() => {
                onClone({ ...getValues(fieldName) });
              }}
            />
            <CircleButton
              disabled={timeWindows.length === 1}
              key='delete'
              data-testid={`${fieldName}-delete`}
              icon={<Delete size='7px' />}
              title='Delete'
              onClick={() => {
                onRemove(index);
              }}
            />
          </>
        )}
        {!isOpened && hasErrors && (
          <Warning size='13px' className='m-1' fill='#AA0101' />
        )}
      </div>
    </div>
  );
};
